/**
 * @lume/shared — Prazo TIPADO com POLARIDADE (Motor Neutro B3, eixo 3 da Fase 2).
 *
 * Primitiva SEPARADA, decisão travada da Fase 2 (estagio5 §4 + estagio6 §2.5): o prazo
 * **NÃO participa** da decisão «pode fazer isso agora» — é consequência/projeção da relação,
 * consumida por Pendency/scheduling e auditada à parte. ⇒ fronteira B3 × Frente A LIMPA:
 * `evaluateIdentityGate` e `access-decision.ts` são INTOCADOS por esta fatia.
 *
 * ★ O QUE A AUDITORIA NA FONTE ACHOU (spec §0): o eixo 3 **não estava ausente** — está
 * implementado ~25 vezes, com a polaridade CRAVADA na lógica de cada módulo. Em 100% dos
 * mecanismos a config expõe só o NÚMERO; o que acontece ao vencer é sempre código. Dos 5
 * campos da tupla que a Fase 2 pediu (`{gatilho, cômputo, polaridade, severidade, dono}`),
 * **4 já existem no SLA** (`SlaDefinition.slaType`/`thresholdMinutes` + `SlaSeverity` +
 * `SlaResourceType`) — mas o SLA é estruturalmente MONOPOLAR (só sabe detectar violação e
 * acumular `SlaBreach`). O que falta é exatamente a POLARIDADE.
 *
 * ★ O CUSTO de não haver o tipo, hoje: **8 COLUNAS de prazo sem motor**
 * (`UNENFORCED_DEADLINE_COLUMNS`, cada uma com motivo escrito). A raiz é a mesma em todas —
 * para escrever a varredura é preciso decidir o que o decorrer SIGNIFICA, e sem tipo isso foi
 * diferido em comentário (`schema.prisma`, enum `CustomerCommitmentStatus`: «deadline passou sem
 * fulfilled (cron pode marcar)»; enum de `PendencyStatus`: «`postponed` reservado p/ R0.B com
 * `postponedUntil` + `PendencyWakeupCron`»).
 *
 * ⚠️ **8 aqui ≠ os «9» da tabela §0.3 da spec — os dois números contam coisas DIFERENTES**
 * (medido na validação viva de 26/Jul; a redação anterior dizia «9» apontando para esta
 * constante, que sempre teve 8, e o roteiro herdou o erro). Esta constante conta só **coluna
 * `DateTime` que É prazo e não tem motor**; a tabela da spec é um inventário MISTO de 9 linhas
 * que inclui 2 VALORES de enum sem cron (`PendencyStatus.expired`, `Guardianship.status='expired'`),
 * 1 coluna **nunca construída** (`Pendency.postponedUntil` — existe só no comentário do enum,
 * conferido: zero ocorrências como campo no `schema.prisma`) e 1 **estampa do passado**
 * (`SlaBreach.resolvedAt`, fora da whitelist de prazo por desenho — declarada na precondição
 * (ii) abaixo). Nada foi perdido do inventário: o guard de drift proíbe mecanicamente coluna
 * sem bucket. Citações por NÚMERO de linha foram trocadas por âncora de texto — as duas
 * anteriores já apontavam para linhas alheias.
 *
 * DORMENTE quanto ao seu INVENTÁRIO: nenhum dos 43 kinds do seed foi ligado. Só o preview
 * read-only (`DeadlineTaxonomyService`), o guard de drift e o espelho consomem o seed.
 *
 * ★ ESTREITADO pela B2c (25/Jul): `resolveDeadlineDisposition` passou a ter um consumidor
 * **VIVO** — a auto-restrição tipada (`self-restriction.ts`), que a usa com
 * `blocks-until-elapsed` no caminho do disparo proativo. O seed, o piso (`applyDeadlinePolarityFloor`)
 * e o guard de drift seguem INTOCADOS, e a B2c cunha um kind **NOVO**
 * (`self-restriction-window`), respeitando a R3. A claim honesta hoje é: o inventário é
 * dormente; a FUNÇÃO de disposição, não mais.
 *
 * ZERO migração, ZERO flag, byte-idêntico por construção. Nasce **AO LADO** do SLA
 * (decisão do TL): `SlaDefinition`/`SlaBreach` e o painel do supervisor INTOCADOS.
 *
 * ★ PRECONDIÇÕES NOMEADAS DA PROMOÇÃO (spec §5) — quem ligar um prazo ou fizer o SLA
 * consumir esta primitiva carrega:
 *   (i)   **unificar com `SlaDefinition`** — hoje o SLA é a casa de `breach-accrues` com 4/5
 *         campos da tupla e escada própria (`SlaScope`, a 5ª escada divergente do repo);
 *         duas casas coexistindo é a dívida que B3 declara, não resolve;
 *   (ii)  **`SlaBreach.resolvedAt` não tem writer** ⇒ `conversation-priority.service.ts:75-81`
 *         filtra `resolvedAt: null` e a prioridade elevada NUNCA decai — ligar mais prazos
 *         sobre esse motor agrava;
 *   (iii) **o cômputo, não só o rótulo** — `computation:'business-days'` DECLARA qual cômputo o
 *         mecanismo usa; não o implementa. Existe UMA implementação no repo, privada e sem
 *         feriados (`lgpd-requests.service.ts:17-29`), e ela alimenta um prazo legal que a
 *         auditoria LGPD marca QUEBRADO ao vivo (15 dias ÚTEIS onde a lei diz corridos);
 *   (iv)  **fuso** — nenhum prazo é tz-aware fora da agenda e do quiet-hours;
 *   (v)   **a autoria** — não há casa DB para declarar polaridade por tenant. Quando houver, o
 *         **portão de afrouxamento** (molde `classifyMappingRisk` da A3) é obrigatório:
 *         rebaixar a polaridade de um prazo é a alavanca de concessão;
 *   (vi)  **R1 é esquivável por OMISSÃO de `action`** (provado por execução na revisão:
 *         `applyDeadlinePolarityFloor('grants-on-silence', {})` sobrevive, porque sem ação não há
 *         consequência a medir). Quem promover a primitiva DEVE tornar `action` obrigatória nos
 *         sites consequentes — hoje a R1 depende da honestidade do caller;
 *   (vii) **o seed é mutável em runtime** — `as const` é garantia de TS, não de runtime (nenhum
 *         seed do repo usa `Object.freeze`; consistente com A2/A3/B1). Num caminho vivo isso
 *         vira vetor: reatribuir `PLATFORM_DEADLINE_SEED['otp-challenge'].polarity` burlaria a
 *         R3. Congelar é trabalho da fatia de promoção, não desta (dormente).
 *
 * Fail-safe: polaridade desconhecida/corrompida ⇒ `breach-accrues` (nunca perde direito, nunca
 * concede); `grants-on-silence` inadmissível em ação consequente; `kind` de plataforma
 * reservado. Puro/total (sem I/O, sem lançar).
 *
 * NEUTRO: `kind` é string OPACA — quem nomeia o prazo é o mecanismo/tenant, não o motor.
 * Zero literal setorial; regulador/contagem-de-dias/valores são config (Fase 2 estagio4).
 *
 * Spec: docs/superpowers/specs/2026-07-25-motor-neutro-b3-prazo-tipado-polaridade-design.md
 */
import { isConsequentialAction } from './risk-category';
// ───────────────────────────────────────────────────────────────────────────────
// §2.1 Vocabulário fechado — as 4 polaridades
// ───────────────────────────────────────────────────────────────────────────────
/**
 * A variável crítica que a Fase 2 (estagio4 §3) identificou como AUSENTE:
 *   - `expires-forfeits`      decorreu ⇒ o direito/oferta/validade MORRE
 *   - `breach-accrues`        decorreu ⇒ acumula violação/sanção; o direito CONTINUA
 *   - `grants-on-silence`     decorreu sem resposta ⇒ considera-se CONCEDIDO
 *   - `blocks-until-elapsed`  a ação é PROIBIDA até decorrer (polaridade INVERTIDA)
 */
export const DEADLINE_POLARITIES = [
    'expires-forfeits',
    'breach-accrues',
    'grants-on-silence',
    'blocks-until-elapsed',
];
export const DEADLINE_SEVERITIES = ['warning', 'critical'];
/**
 * O «dono» da tupla da Fase 2 — quem CARREGA o prazo. Cada valor ancorado num mecanismo real:
 * `operator` (`Pendency.ownerUserId`) · `tenant` (o controlador deve ao titular, prazo LGPD) ·
 * `subject` (o titular perde o código OTP) · `platform` (a plataforma restringe a SI MESMA,
 * quiet-hours) · `counterparty` (o grant de contato autorizado).
 *
 * O eixo é «quem SOFRE ou EXERCE», não «quem configurou» (N3 da revisão): por isso
 * `payment-grace-period` é `tenant` (é o tenant que será suspenso ao fim da graça) enquanto
 * `quiet-hours` é `platform` (a plataforma se cala em favor de um terceiro). Quando os dois
 * lados cabem, vale quem perde algo ao decorrer.
 */
export const DEADLINE_BEARERS = [
    'platform',
    'tenant',
    'operator',
    'subject',
    'counterparty',
];
/**
 * Como o prazo é CONTADO. Catálogo honesto dos padrões que a varredura achou — não uma
 * aspiração: `business-days` tem UMA implementação no repo (privada, sem feriados) e
 * `wall-clock-window` é o único tz-aware. `DAY_MS` está redeclarado 9× no monorepo.
 *
 * `minutes` é o rótulo de TODA contagem sub-diária, inclusive TTLs de segundos (sinal de foco
 * 60s, consentimento 45s, cooldown 120s) — o vocabulário não tem `seconds` de propósito: o campo
 * é DESCRITIVO (diz de que natureza é o cômputo), não a unidade em que o valor é expresso (N2 da
 * revisão). Se algum dia a unidade importar para o consumidor, ela vira campo próprio.
 */
export const DEADLINE_COMPUTATIONS = [
    'absolute',
    'calendar-days',
    'minutes',
    'business-days',
    'wall-clock-window',
];
// ───────────────────────────────────────────────────────────────────────────────
// §2.2 O observável
// ───────────────────────────────────────────────────────────────────────────────
/**
 * O que significa o prazo estar (ou não) decorrido, POR POLARIDADE. Puro/total.
 *
 * ★ É aqui que a inversão vive: para `blocks-until-elapsed`, **não-decorrido ⇒ `blocked`** e
 * **decorrido ⇒ `released`**. Um `deadline: Date` genérico com a comparação `now >= deadline`
 * agiria do LADO ERRADO exatamente nesta linha — o que a Fase 2 §3 previu e a demo DB-backed
 * prova contra o `ProactiveDispatchGuardService` real.
 */
export function resolveDeadlineDisposition(params) {
    if (params.polarity === 'blocks-until-elapsed') {
        return params.elapsed ? 'released' : 'blocked';
    }
    if (!params.elapsed)
        return 'pending';
    switch (params.polarity) {
        case 'expires-forfeits':
            return 'forfeited';
        case 'grants-on-silence':
            return 'granted';
        case 'breach-accrues':
            return 'accruing';
        default:
            // Total em RUNTIME, não só no tipo: um caller que pule `applyDeadlinePolarityFloor` e
            // passe polaridade fora do conjunto recebia `undefined` silencioso. Cai no MESMO
            // fail-safe da R2 (`breach-accrues` ⇒ `accruing`): nunca perde direito, nunca concede.
            return 'accruing';
    }
}
// ───────────────────────────────────────────────────────────────────────────────
// §2.4 O PISO inafrouxável (3 regras) — só ESTREITA, nunca amplia
// ───────────────────────────────────────────────────────────────────────────────
/**
 * `Set` construído em tempo de módulo a partir do `as const` do MESMO arquivo — sem o trap de
 * import circular que a A3 pegou (lá o `Set` vinha de outro módulo via barrel e nascia vazio,
 * virando um falso-conservador). NÃO mover para fora deste arquivo.
 */
const POLARITY_SET = new Set(DEADLINE_POLARITIES);
/**
 * Aplica o piso de plataforma sobre uma polaridade DECLARADA.
 *
 * - **R1 — silêncio nunca concede em ação consequente.** `grants-on-silence` é inadmissível
 *   quando a ação é consequente (reusa `isConsequentialAction` da A2 — a mesma primitiva
 *   agnóstica de caminho que a A4 fiou no gate; ação desconhecida ⇒ consequente, fail-safe
 *   INVERTIDO, logo o desconhecido também é barrado). Isto CODIFICA um piso que hoje existe
 *   de facto e anônimo, reimplementado módulo a módulo: dual-approval vencido ⇒ `expired` +
 *   reject-callback (`dual-approval-expiry.cron.ts:118-130`); consentimento H_c vencido ⇒
 *   `timed_out_passive`/declined (`consent-timeout.worker.ts:8-14`); delegação por etapa ⇒
 *   «o timeout só DEVOLVE ao delegante; nunca efetiva» (`step-delegation.util.ts:35`).
 * - **R2 — desconhecido ⇒ `breach-accrues`**: nunca perde direito, nunca concede
 *   (fail-safe da Decisão 1 regra (f) do DRIVER).
 *
 * `opts.action` ausente ⇒ R1 não se aplica (não há ação cuja consequência medir).
 * (R3 é `isPlatformDeadlineKind`, abaixo — o eixo do NOME, não da polaridade.)
 */
export function applyDeadlinePolarityFloor(declared, opts) {
    // R2 — vale também p/ `null`/número/objeto/chave-de-protótipo: `POLARITY_SET.has` só
    // aceita os 4 literais, e um `Set` não sofre lookup herdado (ao contrário de um mapa-objeto).
    if (declared === undefined || !POLARITY_SET.has(declared))
        return 'breach-accrues';
    // R1
    if (declared === 'grants-on-silence' &&
        opts.action !== undefined &&
        isConsequentialAction(opts.action)) {
        return 'breach-accrues';
    }
    return declared;
}
// ───────────────────────────────────────────────────────────────────────────────
// §2.5 O SEED — o inventário da varredura, como CÓDIGO
// ───────────────────────────────────────────────────────────────────────────────
/**
 * Os prazos REAIS da Lume, classificados por polaridade, com `file:line` de evidência.
 *
 * As chaves são os `kind` **RESERVADOS À PLATAFORMA** (R3): config de tenant só pode cunhar
 * kinds NOVOS, nunca redeclarar a polaridade de um mecanismo de plataforma. **Esta é a lição
 * direta do Blocker da B4** — um piso que barra valores estranhos mas não os do próprio
 * vocabulário da plataforma é um piso furado. Sem R3, um tenant redeclararia `otp-challenge`
 * como `grants-on-silence` e o OTP passaria a conceder-se por silêncio.
 *
 * ★ **LIMITE DE COBERTURA — leia antes de tratar este seed como exaustivo (IMPORTANT-1 da
 * revisão adversarial).** A garantia MECÂNICA vale só para prazos que vivem em **coluna** do
 * `schema.prisma`: o guard de drift (`deadline-schema-drift.spec.ts`) obriga cada coluna a estar
 * classificada aqui ou numa das listas de exclusão. Para os prazos que vivem em **constante, env
 * ou policy Json** (quiet-hours, janela 24h da Meta, TTL de OTP, supressão de foco, cooldowns,
 * timeouts de worker…) **não existe guarda** — eles entram por varredura MANUAL, e a revisão
 * adversarial provou que a varredura inicial perdeu 3 (`conversation-customer-silence`,
 * `churn-cascade-heartbeat`, `mcp-circuit-breaker-open`, todos adicionados). Portanto: ausência
 * de um mecanismo aqui **não** é evidência de que ele não existe. Fechar essa lacuna exigiria um
 * guard sobre constantes de tempo em `src` (todo `* 60 * 1000`/`_MS`/`Sec` que vira decisão) —
 * ruidoso e não tentado nesta fatia; é candidato a fatia própria.
 *
 * `grants-on-silence` NÃO tem nenhuma entrada — e é POSTURA, não esquecimento (spec §0.4):
 * todo prazo de decisão da Lume é negativo por silêncio. Isso trava mercados onde aprovação
 * tácita é a lei (condomínio, renovação em seguros, deemed-consent regulatório); a fatia torna
 * a polaridade REPRESENTÁVEL e INADMISSÍVEL onde é perigosa — o valor é o piso, não a
 * capacidade (§1.4 do DRIVER: não construir capacidade à frente da prova).
 */
export const PLATFORM_DEADLINE_SEED = {
    // ── expires-forfeits — decorreu, o direito morre ─────────────────────────────
    /** `Identity.verifiedExpiresAt`; predicado `link-provenance.ts:56-63`. Declarável: `Tenant.identityVerificationPolicy.ttlDays` (null = sem prazo). */
    'identity-verification-ttl': f('identity-verification-ttl', 'subject', 'calendar-days'),
    /** `VerificationChallenge.expiresAt`; TTL 10min hardcoded `otp-verification.service.ts:23`; transição lazy `:525-530`. */
    'otp-challenge': f('otp-challenge', 'subject', 'minutes'),
    /** `PasswordResetToken.expiresAt`; TTL 1h `password-reset.service.ts` (`PASSWORD_RESET_TTL_MS`); vencido ⇒ 410 `reason: 'expired'` no resgate. */
    'password-reset-link': f('password-reset-link', 'operator', 'minutes'),
    /** `ChannelOnboardingAttempt.expiresAt`; 1 dia (`business_app`) ou 14 dias `onboarding-attempts.service.ts`; vencida sai das abertas e a conexão recomeça. */
    'channel-onboarding-attempt': f('channel-onboarding-attempt', 'tenant', 'calendar-days'),
    /** `exp` do access token; `env.schema.ts:27` (3600s) / `:31` (mobile 900s). Env, não por-tenant. */
    'session-access-token': f('session-access-token', 'subject', 'minutes'),
    /** `exp` do refresh + rotation/reuse-detection `sessions.service.ts:122-147`; `env.schema.ts:28,32`. */
    'session-refresh-token': f('session-refresh-token', 'subject', 'minutes'),
    /** Reabrir a MESMA conversa: 30min `conversations.service.ts:1086,1121`. */
    'conversation-reopen-same': f('conversation-reopen-same', 'subject', 'minutes'),
    /** Reabrir COM vínculo (`reopenedFrom`): 24h `conversations.service.ts:1087,1141`; passou ⇒ conversa órfã. */
    'conversation-reopen-linked': f('conversation-reopen-linked', 'subject', 'minutes'),
    /** `ConversationSequence.inactivityClosesAt`; janela por `bond.type` `conversation-sequences/types.ts:32-56`. */
    'sequence-inactivity': f('sequence-inactivity', 'subject', 'calendar-days'),
    /** Decaimento da memória entre conversas `relationship-memory.util.ts:35-40` (quente→morno→descartado). */
    'relationship-memory-decay': f('relationship-memory-decay', 'platform', 'calendar-days'),
    /** `WaitlistEntry.offerExpiresAt`; reverte e re-oferece ao PRÓXIMO `waitlist.service.ts:692-733`. Declarável: `waitlistPolicy.offerWindowMinutes`. */
    'waitlist-offer': f('waitlist-offer', 'subject', 'minutes'),
    /** `Conversation.delegationWindow.timeoutAt`; `step-delegation.util.ts:110-115`. Devolve ao delegante — NUNCA efetiva (`:35`). */
    'step-delegation-window': f('step-delegation-window', 'operator', 'minutes'),
    /** Janela livre de 24h da Meta `window-24h.ts:17,33`; fora dela só template aprovado (`outbound.service.ts:198-211`). */
    'whatsapp-free-window': f('whatsapp-free-window', 'tenant', 'minutes'),
    /** `DualApprovalRequest.expiresAt`; cron ⇒ `expired` + reject-callback `dual-approval-expiry.cron.ts:118-130`. */
    'dual-approval-request': f('dual-approval-request', 'operator', 'minutes'),
    /** `TemporaryFeatureFlag.effectiveUntil`; cron revoga `temporary-feature-flag-expiry.cron.ts:92-120`. */
    'temporary-capability': f('temporary-capability', 'tenant', 'absolute'),
    /** `OverrideRequest.expiresAt` — cutoff do override; sincronizado com o flag temporário. */
    'override-request-window': f('override-request-window', 'operator', 'absolute'),
    /** `ConductionProposal.expiresAt`; 30min `conduction-proposal.service.ts:12,150`; lazy `:248`. Silêncio NÃO concede H_c. */
    'conduction-proposal': f('conduction-proposal', 'operator', 'minutes'),
    /** `AISuggestion.expiresAt`; 5min `ai-suggestion.service.ts:31`; lazy `:796`. */
    'ai-suggestion': f('ai-suggestion', 'operator', 'minutes'),
    /** TTL do sinal «operador está olhando»: 60s in-memory `conversation-viewing.constants.ts:18`. */
    'viewing-focus-signal': f('viewing-focus-signal', 'operator', 'minutes'),
    /**
     * `BondAuthorizedContact.expiresAt` — mandato voluntário («agir por outro»).
     *
     * ⚠️ **Correção de inventário (B6, 26/Jul).** A redação anterior — «grant vencido não conta
     * `structured-collections.service.ts:196-200`» — citava **UM** leitor como se fosse o
     * comportamento, e por isso esta coluna ficou no lado ENFORÇADO. Auditado leitor a leitor: dos
     * consumidores, `structured-collections` e `appointment-reminder-scheduler` honravam a vigência
     * (cada um com o seu `OR` ad-hoc), o CRUD não honra (correto — superfície de autoria deve
     * MOSTRAR o mandato vencido), e o **feeder do gate de autorização** (`ContactRoleService`) **NÃO
     * honrava** — filtrava só `status:'active'`, exatamente a forma que a lista de NÃO-enforçados
     * atribui ao `LegalHold.expiresAt`. Mesmo defeito, duas classificações.
     *
     * **NENHUM cron vira `status→'expired'`** (o valor de enum existe sem produtor). O enforcement
     * é em tempo de LEITURA, e desde a **B6** ele mora na primitiva neutra `resolveVoluntaryGrant`
     * (`mandate.ts`), consumida pelo feeder do gate: a polaridade `expires-forfeits` deste kind
     * passa a ser honrada onde a decisão é tomada — caducou ⇒ **perde** o escopo, e NÃO cai na
     * política do papel (cair alargaria, porque `resolveEffectiveContactScopes` SUBSTITUI).
     */
    'authorized-contact-grant': f('authorized-contact-grant', 'counterparty', 'absolute'),
    /** Retenção 5/7/10 anos por tier; `retention-policy.worker.ts:125-128` ⇒ re-hasheia o snapshot em claro. */
    'anonymization-retention': f('anonymization-retention', 'tenant', 'calendar-days'),
    /** `Tenant.churningCancellableUntil`; recusa o cancelamento após a janela `tenant-lifecycle.service.ts:332-336`. */
    'churn-cancellation-window': f('churn-cancellation-window', 'tenant', 'absolute'),
    /** Consentimento de escalação H_c; `Tenant.hCEscalationConsentTimeoutSec` (45s) + `consent-timeout.worker.ts`. Silêncio = declined. */
    'escalation-consent': f('escalation-consent', 'subject', 'minutes'),
    /** ConflictAlert low/medium parado > 30d ⇒ `auto_resolved` (`conflict-alert-auto-resolve.cron.ts`); high/critical NUNCA. */
    'conflict-alert-stale': f('conflict-alert-stale', 'operator', 'calendar-days'),
    /** `WorkingHours.effectiveUntil` — a janela deixa de existir (`scheduling/internal.ts:31`). Par bipolar de `working-hours-onset`. */
    'working-hours-expiry': f('working-hours-expiry', 'tenant', 'absolute'),
    /** `Guardianship.effectiveUntil` — a delegação cai, volta a `self` (`decision-authority.ts:112`, B2). Par bipolar de `guardianship-onset`. */
    'guardianship-expiry': f('guardianship-expiry', 'counterparty', 'absolute'),
    /**
     * `OutboundTaskRoot.closePolicy.deadlineAt` — decorreu ⇒ consolida em `awaiting_review` SEM
     * os filhos pendentes (`outbound-task-root-lifecycle.util.ts:35-41`). É o VIZINHO
     * ESTRUTURAL mais próximo de `grants-on-silence` no repo, mas NÃO é: nenhum direito é
     * concedido a ninguém e o fecho segue HUMANO (`:17` «o máximo automático é awaiting_review,
     * nunca closed»). Quem for implementar aprovação tácita começa a ler por aqui.
     */
    'outbound-campaign-close-deadline': f('outbound-campaign-close-deadline', 'operator', 'absolute'),
    // ── breach-accrues — decorreu, acumula sanção; o direito continua ────────────
    /** `Pendency.deadline`; overdue é flag DERIVADA (`pendencies.serialization.ts:67`), o cron só audita+notifica (`pendency-overdue-warning.cron.ts`) — NÃO muda o status. */
    'pendency-deadline': a('pendency-deadline', 'operator', 'absolute', 'warning'),
    /** `SlaDefinition.thresholdMinutes` ⇒ `SlaBreach` + prioridade + notificação (`sla-breach-detection.service.ts:335-405`). Declarável por tenant (CRUD). O SLA é a casa desta polaridade. */
    'sla-threshold': a('sla-threshold', 'operator', 'minutes', 'warning'),
    /** Motor de SLA PARALELO do ConflictAlert, thresholds hardcoded `conflict-alert-sla-breach.cron.ts:28-33` — não fala com `SlaDefinition`. */
    'conflict-alert-sla': a('conflict-alert-sla', 'operator', 'minutes', 'critical'),
    /**
     * `LgpdRequest.legalDeadlineAt`. Vencer só acende `isOverdue` (`lgpd-requests.service.ts:171`)
     * — nada bloqueia, nada expira; o workflow segue. ⚠️ O cômputo é `business-days` (15 dias
     * ÚTEIS, `:52,186`) e a auditoria LGPD o marca **QUEBRADO ao vivo** (a lei diz 15 corridos;
     * a extensão +15 úteis não tem amparo no art. 19). B3 apenas CLASSIFICA — o conserto é
     * decisão jurídica (frente F-LGPD/B5).
     */
    'lgpd-request-legal-deadline': a('lgpd-request-legal-deadline', 'tenant', 'business-days', 'critical'),
    /** `isCadastralReviewDue` (`verification-policy.ts:133`) — vence ⇒ Pendency de revisão, NUNCA re-verificação. Declarável (`cadastralReviewPolicy.reviewDays`). Hoje INERTE (sem anchor/sweep — o próprio arquivo declara o enforcement como follow-up). */
    'cadastral-review': a('cadastral-review', 'tenant', 'calendar-days', 'warning'),
    /** Penalidade de no-show: janela DESLIZANTE (`waitlist.service.ts:1686`) rebaixa o tier com clamp que nunca promove (`waitlist-match.ts:92-109`); a falta CADUCA ao sair da janela. */
    'no-show-penalty': a('no-show-penalty', 'subject', 'calendar-days', 'warning'),
    // ── blocks-until-elapsed — a ação é PROIBIDA até decorrer (INVERTIDA) ────────
    /** Quiet-hours: `isWithinQuietHours`/`computeQuietHoursDeferral` (`proactive-communication-policy.ts:148-179`) ⇒ `defer` — «adia, não perde». Único tz-aware junto da agenda (fuso do RECURSO). Declarável (`proactiveCommunicationPolicy`). */
    'quiet-hours': b('quiet-hours', 'platform', 'wall-clock-window'),
    /** Supressão da IA enquanto o operador olha: `shouldReleaseHeldTurn` (`viewing-suppression.ts:94-115`); ESTENDE o bloqueio enquanto ele digita. Declarável (`conversationViewingPolicy.suppressionTimeoutSec`). */
    'viewing-suppression': b('viewing-suppression', 'operator', 'minutes'),
    /** Cooldown do alerta «cliente esperando»: 120s (`conversation-viewing.constants.ts:29`) — novo alerta proibido até decorrer. */
    'customer-waiting-alert-cooldown': b('customer-waiting-alert-cooldown', 'platform', 'minutes'),
    /** `Tenant.paymentOverdueGracePeriodEndsAt` — a SUSPENSÃO é proibida enquanto a graça corre (`tenant-lifecycle.worker.ts:81-108`). */
    'payment-grace-period': b('payment-grace-period', 'tenant', 'absolute'),
    /** `WorkingHours.effectiveFrom` — antes dela a janela não vale ⇒ zero slots, booking rejeitado (`scheduling/internal.ts:30`). Par bipolar de `working-hours-expiry`. */
    'working-hours-onset': b('working-hours-onset', 'tenant', 'absolute'),
    /** `Guardianship.effectiveFrom` — poder negado até a data chegar (`decision-authority.ts:108`, B2). Par bipolar de `guardianship-expiry`. */
    'guardianship-onset': b('guardianship-onset', 'counterparty', 'absolute'),
    /** `ServiceOffering.minLeadTimeMin` — proíbe agendar slot cuja distância até agora é menor que o mínimo (`assert-bookable.ts:28-29`). */
    'appointment-min-lead-time': b('appointment-min-lead-time', 'tenant', 'minutes'),
    /** `AvailabilityBlock.startsAt..endsAt` — agenda fechada no intervalo (`compute-availability.ts:102`). */
    'availability-block': b('availability-block', 'tenant', 'absolute'),
    /**
     * `LegalHold` — anonimização PROIBIDA enquanto o hold vive (`anonymization.service.ts:169-173`
     * lança; `retention-policy.worker.ts:140-152` pula). ⚠️ O «decorrer» nunca acontece por
     * TEMPO: `LegalHold.expiresAt` não é lido por ninguém (ver `UNENFORCED_DEADLINE_COLUMNS`) —
     * só o `lift()` manual libera.
     */
    'legal-hold': b('legal-hold', 'tenant', 'absolute'),
    /** Circuit breaker MCP: chamadas PROIBIDAS por 5min após 5 falhas em 10min (`mcp/circuit-breaker/mcp-circuit-breaker.service.ts:19,39`). Mesma forma do cooldown de alerta. */
    'mcp-circuit-breaker-open': b('mcp-circuit-breaker-open', 'platform', 'minutes'),
    // ── Achados do IMPORTANT-1 da revisão adversarial (prazos SEM coluna que a
    //    varredura inicial perdeu — ver a nota de LIMITE no docblock deste seed) ──
    /**
     * Timeout de conversa `awaiting_customer`: D-6 avisa, **D-7 FECHA** com
     * `outcomeType='unresolved_abandoned_by_customer'` (`lifecycle/cron/conversation-timeout.worker.ts:12-13,103-114`).
     * Prazo de PRODUTO — o cliente perde a conversa aberta. Irmão de `sequence-inactivity`, que já
     * estava no seed: a omissão deste era INCONSISTENTE, não escolha de escopo (IMPORTANT-1).
     */
    'conversation-customer-silence': f('conversation-customer-silence', 'subject', 'calendar-days'),
    /**
     * Churning: sem heartbeat do worker de cascade por 90d ⇒ transiciona a `churned`
     * («considera cascade concluído», `tenant-lifecycle.worker.ts:112-122`). É o **2º vizinho
     * estrutural** de `grants-on-silence` no repo (avança o processo pela AUSÊNCIA de sinal) —
     * mas, como o 1º (`outbound-campaign-close-deadline`), não concede direito a ninguém: o que
     * decorre é a chance do worker de reportar.
     */
    'churn-cascade-heartbeat': f('churn-cascade-heartbeat', 'platform', 'calendar-days'),
};
/**
 * R3 — o `kind` é reservado à plataforma? `Object.hasOwn` blinda contra chaves herdadas do
 * protótipo (`constructor`/`toString`/`__proto__`), a classe de furo que o C3 M2 corrigiu.
 */
export function isPlatformDeadlineKind(kind) {
    return Object.hasOwn(PLATFORM_DEADLINE_SEED, kind);
}
// Construtores locais — mantêm o seed legível e impedem `severity` fora de `breach-accrues`
// por CONSTRUÇÃO (não só por teste).
function f(kind, bearer, computation) {
    return { kind, polarity: 'expires-forfeits', bearer, computation };
}
function a(kind, bearer, computation, severity) {
    return { kind, polarity: 'breach-accrues', severity, bearer, computation };
}
function b(kind, bearer, computation) {
    return { kind, polarity: 'blocks-until-elapsed', bearer, computation };
}
// ───────────────────────────────────────────────────────────────────────────────
// §2.6 As listas de exclusão — o residual honesto, MECÂNICO (guard de drift)
// ───────────────────────────────────────────────────────────────────────────────
/**
 * Coluna do `schema.prisma` → o `kind` do seed que a governa. Consumido pelo GUARD DE DRIFT
 * (`deadline-schema-drift.spec.ts`), que exige que TODA coluna de prazo do schema esteja aqui
 * ou numa das duas listas de exclusão abaixo — coluna nova ⇒ teste vermelho até a polaridade
 * ser decidida. Molde A7: a autoridade passa a ser o código, enforçada mecanicamente.
 *
 * Kinds do seed SEM coluna (quiet-hours, whatsapp-free-window, viewing-suppression, os dois
 * *-onset…) são prazos que vivem em constante/policy Json, não em coluna — não aparecem aqui.
 */
export const DEADLINE_COLUMN_KIND = {
    'AISuggestion.expiresAt': 'ai-suggestion',
    'BondAuthorizedContact.expiresAt': 'authorized-contact-grant',
    'ChannelOnboardingAttempt.expiresAt': 'channel-onboarding-attempt',
    'ConductionProposal.expiresAt': 'conduction-proposal',
    'ConversationSequence.inactivityClosesAt': 'sequence-inactivity',
    'DualApprovalRequest.expiresAt': 'dual-approval-request',
    'Guardianship.effectiveUntil': 'guardianship-expiry',
    'Identity.verifiedExpiresAt': 'identity-verification-ttl',
    'LgpdRequest.legalDeadlineAt': 'lgpd-request-legal-deadline',
    'OverrideRequest.expiresAt': 'override-request-window',
    'PasswordResetToken.expiresAt': 'password-reset-link',
    'Pendency.deadline': 'pendency-deadline',
    'TemporaryFeatureFlag.effectiveUntil': 'temporary-capability',
    'Tenant.churningCancellableUntil': 'churn-cancellation-window',
    'Tenant.paymentOverdueGracePeriodEndsAt': 'payment-grace-period',
    'VerificationChallenge.expiresAt': 'otp-challenge',
    'WaitlistEntry.offerExpiresAt': 'waitlist-offer',
    'WorkingHours.effectiveUntil': 'working-hours-expiry',
};
/**
 * Coluna que É prazo real mas **NÃO tem motor hoje** (spec §0.3). A raiz é a mesma em todas:
 * para escrever a varredura é preciso decidir o que o decorrer SIGNIFICA, e sem tipo isso foi
 * diferido em comentário. Motivo obrigatório (o guard de drift exige ≥ 20 caracteres).
 */
export const UNENFORCED_DEADLINE_COLUMNS = {
    'CustomerCommitment.dueDate': 'nunca comparado a now(); status broken tem ZERO writers programáticos (só PATCH manual). schema.prisma:4794 admite "cron pode marcar" — a promessa vencida nunca vira quebrada',
    'Conversation.reopenWindowExpiresAt': 'coluna ÓRFÃ — nem escrita nem lida em src; o caminho real usa 30min/24h fixos em conversations.service.ts:1086-1087',
    'LegalHold.expiresAt': 'os leitores filtram só status:active; um hold vencido bloqueia a anonimização indefinidamente até lift() manual. Nenhum cron o lê',
    'LegalCapacity.effectiveUntil': 'write-only — o motor de autoridade da B2 lê Guardianship (status + janela), nunca LegalCapacity.effectiveUntil',
    'LegalCapacity.reviewDate': 'write-only — nenhum cron nem serviço lê a data de revisão da capacidade legal',
    'ConflictAlert.acceptanceExpiresAt': 'write-only — 2 hits em src, ambos no caminho de escrita (conflict-alert-lifecycle.service.ts:294,307); nada reavalia a aceitação temporária',
    'InviteToken.expiresAt': 'create-only porque o fluxo de ACEITE não existe (users.service.ts:290 "P4 placeholder: SMTP NÃO materializado") — TTL escrito para um consumidor que ainda não nasceu',
    'User.onLeaveUntil': 'serialize-only (users.serialization.ts:79) — nada roteia trabalho para longe do operador de licença nem o devolve ao fim dela',
};
/**
 * Coluna `DateTime` que **NÃO é prazo** — marcador ou estampa do passado. Não tem polaridade
 * porque não há nada a decorrer. Separada da lista acima de propósito: conflacionar «prazo sem
 * motor» com «não é prazo» esconderia dívida real.
 */
export const NOT_A_DEADLINE_COLUMNS = {
    'Person.proactiveOptOutAt': 'marca DESDE QUANDO o titular pediu para parar — bloqueio perpétuo, sem termo; nenhum código compara o timestamp, só a presença. É flag, não prazo',
    'LgpdRequest.deadlineExtendedAt': 'estampa de QUANDO a extensão foi concedida (auditoria); o prazo em si é legalDeadlineAt',
    'Pendency.overdueNotifiedForDeadline': 'marca QUAL prazo já foi avisado, para o varredor não repetir o aviso — o prazo em si é Pendency.deadline. Não decorre nada: é comparada por IGUALDADE com o deadline vigente, e é justamente isso que a faz RE-ARMAR sozinha quando o prazo é estendido (fatia «promessa quebrada»)',
};
//# sourceMappingURL=typed-deadline.js.map